import { useEffect, useState } from 'preact/hooks';
import { DashboardNav, ErrorNote, Loading } from './Dashboard';
import { ApiError, dashboardApi, type Lead, type LeadRecipient } from './dashboardApi';

// ============================================================================
// Leads — /app/leads. Every contact a visitor left through any of the
// account's chatbots (the pre-chat form or the assistant's contact form),
// newest first, with when it came in and a link to the conversation it
// came from. Each one was also emailed to the account's address the moment
// it arrived; the Email column says whether that worked, because a lead
// whose email bounced is exactly the one the owner needs to see here.
//
// Leads are deleted with their conversation (server/src/leads/), so this
// list follows each chatbot's own retention setting.

const PAGE_SIZE = 50;

function fmtWhen(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const EMAIL_STATUS: Record<Lead['notifyStatus'], { label: string; tone: string }> = {
  sent: { label: 'Emailed', tone: 'db-badge-ready' },
  pending: { label: 'Sending', tone: 'db-badge-pending' },
  failed: { label: 'Not sent', tone: 'db-badge-failed' },
  skipped: { label: 'Not sent', tone: 'db-badge-warn' },
};

function conversationHref(lead: Lead): string {
  return `/app/bots/${encodeURIComponent(lead.botId)}?tab=conversations&conversation=${encodeURIComponent(lead.conversationId)}`;
}

/**
 * Who gets lead alerts: the account's own address, plus up to three more
 * (server/src/leads/recipients.ts). An added address gets a confirmation
 * email and receives nothing until it confirms; removing one stops it at
 * once. They never get dashboard access.
 */
function LeadRecipients() {
  const [data, setData] = useState<{ recipients: LeadRecipient[]; max: number; ownerEmail: string } | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function load() {
    try {
      setData(await dashboardApi.listLeadRecipients());
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't load who gets lead alerts.");
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function run(key: string, action: () => Promise<unknown>, done: string) {
    setBusy(key);
    setError(null);
    setNote(null);
    try {
      await action();
      setNote(done);
      await load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work — please try again.');
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function add(e: Event) {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    const ok = await run('add', () => dashboardApi.addLeadRecipient(address), `We've sent a confirmation email to ${address}. Alerts start once they confirm.`);
    if (ok) setEmail('');
  }

  if (!data) return error ? <ErrorNote error={error} /> : null;
  const full = data.recipients.length >= data.max;

  return (
    <div class="db-recipients">
      <h2 class="db-recipients-h2">Who gets lead alerts</h2>
      <p class="db-sub">
        Add up to {data.max} more addresses to get every lead by email. They don't get a VolgaChat account or see your dashboard. Each
        address has to confirm before alerts start, and can stop them from a link in any alert.
      </p>
      <ul class="db-recipient-list">
        <li>
          <span class="db-recipient-email">{data.ownerEmail}</span>
          <span class="db-badge db-badge-ready">You</span>
        </li>
        {data.recipients.map((r) => (
          <li key={r.id}>
            <span class="db-recipient-email">{r.email}</span>
            {r.confirmedAt ? (
              <span class="db-badge db-badge-ready">Receiving alerts</span>
            ) : (
              <span class="db-badge db-badge-pending">Waiting for confirmation</span>
            )}
            <span class="db-recipient-actions">
              {!r.confirmedAt && (
                <button
                  type="button"
                  class="db-btn-sm"
                  disabled={busy !== null}
                  onClick={() => run(`resend:${r.id}`, () => dashboardApi.resendLeadRecipient(r.id), `Confirmation email sent to ${r.email} again.`)}
                >
                  {busy === `resend:${r.id}` ? 'Sending…' : 'Resend'}
                </button>
              )}
              <button
                type="button"
                class="db-btn-sm"
                disabled={busy !== null}
                onClick={() => run(`remove:${r.id}`, () => dashboardApi.removeLeadRecipient(r.id), `${r.email} won't get any more lead alerts.`)}
              >
                {busy === `remove:${r.id}` ? 'Removing…' : 'Remove'}
              </button>
            </span>
          </li>
        ))}
      </ul>
      {full ? (
        <p class="db-muted">You've added {data.max} addresses — the most there can be. Remove one to add another.</p>
      ) : (
        <form class="db-inline-form" onSubmit={add}>
          <input
            class="db-input"
            type="email"
            placeholder="colleague@yourcompany.com"
            aria-label="Email address to receive lead alerts"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
          <button type="submit" class="site-btn site-btn-primary" disabled={busy !== null || !email.trim()}>
            {busy === 'add' ? 'Adding…' : 'Add address'}
          </button>
        </form>
      )}
      {note && (
        <p class="db-recipient-note" role="status">
          {note}
        </p>
      )}
      <ErrorNote error={error} />
    </div>
  );
}

export function LeadsPage() {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Bumped by "Try again": one failed load used to leave only the error,
  // with no pager and no way to retry short of reloading the page.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setLeads(null);
    setError(null);
    dashboardApi
      .listLeads({ limit: PAGE_SIZE, offset })
      .then((res) => {
        if (!live) return;
        setLeads(res.leads);
        setTotal(res.total);
      })
      .catch((err) => {
        if (!live) return;
        // Our fault or the connection's: say so in this page's terms. A
        // refusal with its own explanation (signed out) keeps it.
        const ours = !(err instanceof ApiError) || err.status === 0 || err.status >= 500;
        setError(
          ours
            ? "We couldn't load your leads just now. Your leads are safe — please refresh the page, or try again in a minute. If it keeps happening, email support@volgachat.com."
            : err.message,
        );
      });
    return () => {
      live = false;
    };
  }, [offset, attempt]);

  return (
    <section class="site-section db-page">
      <div class="site-wrap">
        <DashboardNav current="leads" />
        <h1>Leads</h1>
        <p class="db-sub">
          Everyone who left their contact details with one of your chatbots. Each lead is emailed to you as it arrives, and kept
          here for as long as its conversation is.
        </p>
        <LeadRecipients />
        <ErrorNote error={error} />

        {leads === null ? (
          error ? (
            <button type="button" class="db-btn-sm" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          ) : (
            <Loading label="Loading your leads…" />
          )
        ) : leads.length === 0 && offset === 0 ? (
          <div class="site-empty-state">
            <p>
              No leads yet. Turn on the contact form in a chatbot's <strong>Interactions</strong> tab, and the details visitors leave
              will appear here.
            </p>
          </div>
        ) : (
          <>
            <div class="db-table-wrap">
              <table class="db-table db-leads">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Contact</th>
                    <th>Chatbot</th>
                    <th>Email</th>
                    <th>Conversation</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => {
                    const status = EMAIL_STATUS[lead.notifyStatus];
                    return (
                      <tr key={lead.id}>
                        <td class="db-leads-when">{fmtWhen(lead.createdAt)}</td>
                        <td>
                          <dl class="db-leads-fields">
                            {lead.fields.map((f) => (
                              <div key={f.label}>
                                <dt>{f.label}</dt>
                                <dd>
                                  {f.value.includes('@') && !f.value.includes(' ') ? (
                                    <a href={`mailto:${f.value}`}>{f.value}</a>
                                  ) : (
                                    f.value
                                  )}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </td>
                        <td>{lead.botName}</td>
                        <td>
                          <span class={`db-badge ${status.tone}`} title={lead.notifyError ?? undefined}>
                            {status.label}
                          </span>
                        </td>
                        <td>
                          <a href={conversationHref(lead)}>View conversation</a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div class="db-pager">
                <button type="button" class="db-btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                  Newer
                </button>
                <span class="db-muted">
                  {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                </span>
                <button
                  type="button"
                  class="db-btn-sm"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Older
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
